import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BadgeCheck,
  Download,
  FileCode2,
  MoreVertical,
  PackagePlus,
  Plug,
  Search,
  Store,
  Trash2,
  Users,
  Wrench,
} from "lucide-react";
import { Card, CardBody } from "../components/ui/Card";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { Modal } from "../components/ui/Modal";
import { Spinner } from "../components/ui/Spinner";
import { EmptyState } from "../components/ui/EmptyState";
import { Field, Input } from "../components/ui/Input";
import { useAuth } from "../lib/auth";
import { useExchange } from "../lib/hooks";
import { ApiError, installListing, setListingOfficial, unpublishListing } from "../lib/api";
import { CATEGORY_LABELS } from "../lib/api";
import type { ExchangeListing, ExchangeScope } from "../lib/api";
import { SystemLogo } from "../components/connector/SystemLogo";
import { cn } from "../lib/cn";

function InstallModal({
  listing,
  tenant,
  onClose,
}: {
  listing: ExchangeListing | null;
  tenant: string;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const open = listing !== null;

  // Reset the form each time a listing is picked.
  const key = listing?.slug ?? "";
  const [lastKey, setLastKey] = useState(key);
  if (key !== lastKey) {
    setLastKey(key);
    setSlug(listing?.slug ?? "");
    setName(listing?.display_name ?? "");
    setError(null);
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!listing) return;
    setBusy(true);
    setError(null);
    try {
      const conn = await installListing(tenant, listing.slug, {
        slug: slug || undefined,
        display_name: name || undefined,
      });
      navigate(`/app/connections/${conn.slug}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't reach the server");
      setBusy(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={`Install ${listing?.display_name ?? ""}`}>
      <form onSubmit={onSubmit}>
        <div className="space-y-4 px-6 py-5">
          <p className="text-sm text-slate-500">
            Adds this connector to <span className="font-semibold text-slate-700">this workspace</span> with
            its capabilities and parameter schema. You'll configure endpoints and credentials next —
            nothing is shared from the publisher's workspace.
          </p>
          {error && (
            <div className="rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700 ring-1 ring-inset ring-rose-100">
              {error}
            </div>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Connection slug">
              <Input value={slug} onChange={(e) => setSlug(e.target.value)} pattern="[a-z0-9][a-z0-9\-]*" required />
            </Field>
            <Field label="Display name">
              <Input value={name} onChange={(e) => setName(e.target.value)} required />
            </Field>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 border-t border-slate-100 px-6 py-4">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={busy}>
            <PackagePlus className="h-4 w-4" /> Install & configure
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function MetaChip({
  icon: Icon,
  children,
  tone = "slate",
}: {
  icon: typeof Download;
  children: React.ReactNode;
  tone?: "slate" | "emerald";
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-[11px] font-medium",
        tone === "emerald" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500",
      )}
    >
      <Icon className="h-3 w-3" />
      {children}
    </span>
  );
}

function ListingCard({
  l,
  mine,
  canCurate,
  onInstall,
  onUnpublish,
  onToggleOfficial,
}: {
  l: ExchangeListing;
  mine: boolean;
  canCurate: boolean;
  onInstall: () => void;
  onUnpublish: () => void;
  onToggleOfficial: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const canManage = mine || canCurate;
  // A soft wash of the system's own colour behind the logo tile.
  const tint = l.brand_color ? `#${l.brand_color.replace(/^#/, "")}` : undefined;

  return (
    <Card className="group relative flex h-full flex-col overflow-hidden transition-all duration-200 hover:-translate-y-1 hover:shadow-lift">
      {/* Official entries get a brand rule along the top edge. */}
      {l.is_official && <span className="h-1 w-full shrink-0 bg-brand-gradient" />}

      <CardBody className="flex flex-1 flex-col p-5">
        <div className="flex items-start justify-between gap-3">
          <span
            className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl ring-1 ring-slate-200/80 transition-transform duration-200 group-hover:scale-105"
            style={{ backgroundColor: tint ? `${tint}12` : undefined }}
          >
            {l.is_official ? (
              <SystemLogo
                iconSlug={l.icon_slug}
                systemType={l.system_type}
                brandColor={l.brand_color}
                name={l.display_name}
                size={28}
              />
            ) : (
              <Plug className="h-6 w-6 text-brand-500" />
            )}
          </span>
          <div className="flex shrink-0 items-center gap-1.5">
            {l.is_official && (
              <span className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2.5 py-1 text-[11px] font-semibold text-brand-700 ring-1 ring-inset ring-brand-100">
                <BadgeCheck className="h-3.5 w-3.5" /> Wise
              </span>
            )}
            {mine && !l.is_official && <Badge tone="violet">Yours</Badge>}
            <span className="rounded-full bg-slate-100 px-2 py-1 text-[11px] font-medium text-slate-400">
              v{l.version}
            </span>
          </div>
        </div>

        <h3 className="mt-4 text-[17px] font-semibold leading-tight text-slate-900">
          {l.display_name}
        </h3>
        <p className="mt-1 text-xs text-slate-400">
          <code className="rounded bg-slate-100 px-1.5 py-0.5 font-medium text-slate-500">
            {l.slug}
          </code>
          {l.category && CATEGORY_LABELS[l.category] && (
            <> · <span className="text-slate-500">{CATEGORY_LABELS[l.category]}</span></>
          )}
          {" "}· by <span className="font-medium text-slate-500">{l.publisher_name || "unknown"}</span>
        </p>

        {/* flex-1 keeps every card's action bar on the same line */}
        <p className="mt-3 line-clamp-2 flex-1 text-sm leading-relaxed text-slate-500">
          {l.description || "No description provided."}
        </p>

        {l.capabilities.length > 0 && (
          <div className="mt-3.5 flex flex-wrap items-center gap-1.5">
            {l.capabilities.slice(0, 3).map((c) => (
              <Badge key={c} tone="brand" className="font-mono text-[10px]">
                {c}
              </Badge>
            ))}
            {l.capabilities.length > 3 && (
              <Badge tone="slate" className="text-[10px]">
                +{l.capabilities.length - 3}
              </Badge>
            )}
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-1.5">
          <MetaChip icon={Download}>
            {l.install_count} install{l.install_count === 1 ? "" : "s"}
          </MetaChip>
          <MetaChip icon={Wrench}>{l.params_schema.length} settings</MetaChip>
          {l.has_code ? (
            <MetaChip icon={FileCode2} tone="emerald">
              tested code
            </MetaChip>
          ) : (
            <MetaChip icon={Wrench}>schema only</MetaChip>
          )}
        </div>

        <div className="mt-5 flex items-center gap-2 border-t border-slate-100 pt-4">
          <Button size="sm" className="flex-1" onClick={onInstall}>
            <Download className="h-4 w-4" /> Install
          </Button>

          {/* Owner/admin actions live behind a menu — inline they overflowed. */}
          {canManage && (
            <div className="relative">
              <button
                onClick={() => setMenuOpen((o) => !o)}
                aria-label="More actions"
                className="focus-ring flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 text-slate-400 transition hover:bg-slate-50 hover:text-slate-600"
              >
                <MoreVertical className="h-4 w-4" />
              </button>
              {menuOpen && (
                <>
                  <button
                    className="fixed inset-0 z-10 cursor-default"
                    onClick={() => setMenuOpen(false)}
                    aria-hidden
                  />
                  <div className="absolute bottom-11 right-0 z-20 w-52 animate-scale-in rounded-xl border border-slate-200 bg-white p-1.5 shadow-lift">
                    {canCurate && (
                      <button
                        onClick={() => {
                          setMenuOpen(false);
                          onToggleOfficial();
                        }}
                        className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm text-slate-600 hover:bg-slate-50"
                      >
                        <BadgeCheck className="h-4 w-4 text-brand-500" />
                        {l.is_official ? "Remove from Wise" : "Make official"}
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        onUnpublish();
                      }}
                      className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm text-rose-600 hover:bg-rose-50"
                    >
                      <Trash2 className="h-4 w-4" /> Unpublish
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

const TABS: { scope: ExchangeScope; label: string; hint: string }[] = [
  { scope: "official", label: "Wise Exchange", hint: "Curated and maintained by us" },
  { scope: "community", label: "From workspaces", hint: "Shared by other teams" },
  { scope: "all", label: "All", hint: "Everything published" },
];

export default function Exchange() {
  const { currentTenant, user } = useAuth();
  const { data, loading, error, reload } = useExchange();
  const [q, setQ] = useState("");
  const [scope, setScope] = useState<ExchangeScope>("official");
  const [category, setCategory] = useState<string>("");
  const [installing, setInstalling] = useState<ExchangeListing | null>(null);
  const canCurate = !!user?.is_platform_admin;

  async function onUnpublish(l: ExchangeListing) {
    if (!currentTenant) return;
    try {
      await unpublishListing(currentTenant.slug, l.slug);
      reload();
    } catch {
      // surfaced implicitly on reload; keep the card
    }
  }

  async function onToggleOfficial(l: ExchangeListing) {
    try {
      await setListingOfficial(l.slug, !l.is_official);
      reload();
    } catch {
      // same: the reload reflects whatever actually happened
    }
  }

  const counts = useMemo(
    () => ({
      official: data?.filter((l) => l.is_official).length ?? 0,
      community: data?.filter((l) => !l.is_official).length ?? 0,
      all: data?.length ?? 0,
    }),
    [data],
  );

  /** Categories present in the current scope, with their counts. */
  const categories = useMemo(() => {
    const inScope = (data ?? []).filter((l) =>
      scope === "all" ? true : scope === "official" ? l.is_official : !l.is_official,
    );
    const counts = new Map<string, number>();
    for (const l of inScope) {
      if (l.category) counts.set(l.category, (counts.get(l.category) ?? 0) + 1);
    }
    return Object.keys(CATEGORY_LABELS)
      .filter((key) => counts.has(key))
      .map((key) => ({ key, label: CATEGORY_LABELS[key], count: counts.get(key)! }));
  }, [data, scope]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const inScope = data
      .filter((l) => (scope === "all" ? true : scope === "official" ? l.is_official : !l.is_official))
      .filter((l) => (category ? l.category === category : true));
    const term = q.trim().toLowerCase();
    if (!term) return inScope;
    return inScope.filter(
      (l) =>
        l.display_name.toLowerCase().includes(term) ||
        l.system_type.toLowerCase().includes(term) ||
        l.description.toLowerCase().includes(term) ||
        l.publisher_name.toLowerCase().includes(term) ||
        l.capabilities.some((c) => c.toLowerCase().includes(term)),
    );
  }, [data, q, scope, category]);

  return (
    <>
      <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-start gap-3.5">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-gradient text-white shadow-lift">
            <Store className="h-6 w-6" />
          </span>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">Connector Exchange</h1>
            <p className="mt-1 max-w-xl text-sm leading-relaxed text-slate-500">
              Start from a ready-made connector instead of building one — install it, then
              configure it with your own credentials.
            </p>
          </div>
        </div>
        <div className="relative w-full shrink-0 lg:w-72">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search connectors…"
            className="pl-10"
          />
        </div>
      </div>

      {/* Wise Exchange vs what other workspaces have shared */}
      <div className="mt-7 flex flex-wrap items-center gap-2">
        {TABS.map((t) => {
          const active = scope === t.scope;
          return (
            <button
              key={t.scope}
              onClick={() => setScope(t.scope)}
              title={t.hint}
              className={cn(
                "focus-ring inline-flex items-center gap-2 rounded-xl border px-3.5 py-2 text-sm font-medium transition",
                active
                  ? "border-slate-900 bg-slate-900 text-white shadow-soft"
                  : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900",
              )}
            >
              {t.scope === "official" ? (
                <BadgeCheck className="h-4 w-4" />
              ) : t.scope === "community" ? (
                <Users className="h-4 w-4" />
              ) : (
                <Store className="h-4 w-4" />
              )}
              {t.label}
              <span className={cn("text-xs", active ? "text-white/60" : "text-slate-400")}>
                {counts[t.scope]}
              </span>
            </button>
          );
        })}
      </div>

      {categories.length > 1 && (
        <div className="mt-4 flex flex-wrap items-center gap-1.5">
          <button
            onClick={() => setCategory("")}
            className={cn(
              "focus-ring rounded-lg px-2.5 py-1 text-xs font-medium transition",
              category === ""
                ? "bg-brand-600 text-white"
                : "bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700",
            )}
          >
            All categories
          </button>
          {categories.map((c) => (
            <button
              key={c.key}
              onClick={() => setCategory(category === c.key ? "" : c.key)}
              className={cn(
                "focus-ring rounded-lg px-2.5 py-1 text-xs font-medium transition",
                category === c.key
                  ? "bg-brand-600 text-white"
                  : "bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700",
              )}
            >
              {c.label}
              <span className={cn("ml-1.5", category === c.key ? "text-white/60" : "text-slate-400")}>
                {c.count}
              </span>
            </button>
          ))}
        </div>
      )}

      {scope === "official" && (
        <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-brand-100 bg-brand-50/50 px-4 py-3">
          <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-brand-500" />
          <p className="text-sm leading-relaxed text-slate-600">
            Published and maintained by Wise 2.0, and available to every workspace. Install one
            and you go straight to entering your credentials.
          </p>
        </div>
      )}

      <div className="mt-7">
        {loading ? (
          <div className="flex justify-center py-20">
            <Spinner className="h-8 w-8 text-brand-500" />
          </div>
        ) : error ? (
          <Card className="px-6 py-16 text-center">
            <p className="font-semibold text-slate-800">Couldn't load the exchange</p>
            <p className="mt-1 text-sm text-slate-400">{error}</p>
          </Card>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={<Store className="h-6 w-6" />}
            title={
              q
                ? "No connectors match your search"
                : scope === "official"
                  ? "The Wise Exchange is empty"
                  : "Nothing published yet"
            }
            description={
              q
                ? "Try a different term, or switch tabs to search the whole exchange."
                : scope === "official"
                  ? "Curated connectors will appear here. A platform administrator publishes them from a connection's detail page."
                  : "Publish any connection from its detail page and it will appear here for every workspace."
            }
          />
        ) : (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
            {filtered.map((l) => (
              <ListingCard
                key={l.id}
                l={l}
                mine={!!l.publisher_tenant_id && l.publisher_tenant_id === currentTenant?.id}
                canCurate={canCurate}
                onInstall={() => setInstalling(l)}
                onUnpublish={() => onUnpublish(l)}
                onToggleOfficial={() => onToggleOfficial(l)}
              />
            ))}
          </div>
        )}
      </div>

      {currentTenant && (
        <InstallModal listing={installing} tenant={currentTenant.slug} onClose={() => setInstalling(null)} />
      )}
    </>
  );
}
